<script setup>
import { computed, ref, onMounted } from 'vue';
import { useSafety, apiMessage } from '~/composables/useSafety';
import ReportDialog from '~/components/messaging/ReportDialog.vue';
import { blockConfirmation } from '~/composables/useSafetyCopy.js';
import { useAuthService } from '~/composables/useAuthService';
import { useMessaging } from '~/composables/useMessaging';
import { MessageType } from '~/constants/messageTypes';
import { isTombstone, tombstoneText } from '~/composables/useMessageTombstone.js';
import { canDeleteForEveryone, DELETE_MESSAGE_COPY } from '~/composables/useMessageDelete.js';
import { messageClockTime } from '~/composables/useMessageTime.js';
import { useDeviceLocale } from '~/composables/useDeviceLocale';
import { useDates } from '~/composables/useDates';
import AvatarImage from '@/components/images/AvatarImage.vue';
import UserCard from '@/components/users/UserCard.vue';

const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  /** The conversation this message belongs to — needed to report it. */
  conversationId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['retry', 'delete', 'blocked', 'delete-for-everyone']);

// ── Report / block (Online Safety Act) ──
// Only on messages someone ELSE sent: you report or block a person, never
// yourself. The block button reads from the shared blocked list so every
// bubble agrees without a request each.
const {isBlocked, block, unblock, loadBlocked} = useSafety();
const {setAlertMessage} = useAlertService();
const showReportDialog = ref(false);
const blockBusy = ref(false);
const senderBlocked = computed(() => isBlocked(props.message.senderUserId));
// A block hides every message from that person in every shared chat, so it is
// said first and confirmed (owner finding, 24 Sep 2026). Unblocking needs no
// question: it only gives back.
const confirmingBlock = ref(false);
const blockCopy = computed(() => blockConfirmation(senderName.value));
const toggleBlock = async () => {
  const id = props.message.senderUserId;
  if (!id || blockBusy.value) return;
  if (!senderBlocked.value) {
    confirmingBlock.value = true;
    return;
  }
  blockBusy.value = true;
  try {
    await unblock(id);
    setAlertMessage(`${senderName.value} unblocked. Their messages will show again.`);
  } catch (e) {
    setAlertMessage(apiMessage(e, "Couldn't do that. Please try again."));
  } finally {
    blockBusy.value = false;
  }
};
const confirmBlock = async () => {
  const id = props.message.senderUserId;
  blockBusy.value = true;
  try {
    await block(id);
    confirmingBlock.value = false;
    setAlertMessage(`${senderName.value} blocked. Their messages are hidden.`);
    emit('blocked', id);
  } catch (e) {
    setAlertMessage(apiMessage(e, "Couldn't do that. Please try again."));
  } finally {
    blockBusy.value = false;
  }
};
onMounted(() => { loadBlocked(); });

const { user } = useAuthService();
const { getUsers } = useMessaging();
const { uploadFilenameToUrl } = useCrudConverters();

// Load read by users on demand
const readByUsers = ref([]);
const loadingReadByUsers = ref(false);
const showReadByDialog = ref(false);

// Image lightbox
const showLightbox = ref(false);

// A deleted message keeps its place as a placeholder (24 Sep 2026): no words,
// no photo, no menu, nothing to copy.
const tombstone = computed(() => isTombstone(props.message));

// Deleting my own sent message is always for everyone (owner decision,
// 24 Sep 2026): the confirmation says so, and there is no "delete for me".
const canDelete = computed(() => canDeleteForEveryone(props.message, user.value?.id));
const confirmingDelete = ref(false);
const confirmDelete = () => {
  confirmingDelete.value = false;
  emit('delete-for-everyone', props.message);
};

// Check if message is an image
const isImageMessage = computed(() => {
  return props.message.messageType === MessageType.IMAGE;
});

// Get image URL based on message state
const imageUrl = computed(() => {
  if (!isImageMessage.value) return null;

  // For optimistic messages with local preview
  if (props.message._localImageUrl) {
    return props.message._localImageUrl;
  }

  // For synced messages with imageFileName
  if (props.message.imageFileName) {
    return uploadFilenameToUrl(props.message.imageFileName, 'medium');
  }

  return null;
});

// Get full-size image URL for lightbox
const lightboxImageUrl = computed(() => {
  if (!isImageMessage.value || !props.message.imageFileName) return null;
  return uploadFilenameToUrl(props.message.imageFileName, 'large');
});

// Calculate image aspect ratio for proper sizing
const imageAspectRatio = computed(() => {
  if (!isImageMessage.value) return undefined;
  if (props.message.imageWidth && props.message.imageHeight) {
    return props.message.imageWidth / props.message.imageHeight;
  }
  return undefined;
});

// Check if image is uploading
const isImageUploading = computed(() => {
  return isImageMessage.value && props.message._syncState === 'pending' && !props.message.imageFileName;
});

// Check if image upload failed
const isImageUploadFailed = computed(() => {
  return isImageMessage.value && props.message._syncState === 'failed';
});

const handleReadIndicatorClick = async () => {
  if (!props.message.readByCount || props.message.readByCount === 0) return;

  // Load users if not already loaded
  if (readByUsers.value.length === 0) {
    loadingReadByUsers.value = true;
    try {
      // Filter out current user and sender
      const userIds = props.message.readByUserIds.filter(
        id => id !== user.value?.id && id !== props.message.senderUserId
      );

      if (userIds.length > 0) {
        readByUsers.value = await getUsers(userIds);
      }
    } catch (error) {
      console.error('Error loading read by users:', error);
    } finally {
      loadingReadByUsers.value = false;
    }
  }

  // Show dialog
  showReadByDialog.value = true;
};

// Check if message was sent by current user
const isSent = computed(() => {
  return props.message.senderUserId === user.value?.id;
});

// Sender name
const senderName = computed(() => {
  const sender = props.message.sender;
  if (!sender) return 'Unknown';
  return `${sender.firstName || ''} ${sender.lastName || ''}`.trim();
});

// Sender initials for fallback
const senderInitials = computed(() => {
  const sender = props.message.sender;
  if (!sender) return '?';
  const first = sender.firstName?.charAt(0) || '';
  const last = sender.lastName?.charAt(0) || '';
  return (first + last).toUpperCase() || '?';
});

// The stamp: the clock time only ("18:53"), since the day divider above carries
// the date. Same rule as the app (useMessageTime, TimeAgo.messageTime).
const deviceLocale = useDeviceLocale();
const formattedTime = computed(() =>
  messageClockTime(props.message.createdAt, deviceLocale.value || useDates().currentLocale()));

// Format user name
const formatUserName = (user) => {
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
};

// Unread indicator for received messages
const showUnreadIndicator = computed(() => {
  // Only show for messages we received (not sent)
  if (isSent.value) return false;

  // If isReadByCurrentUser is explicitly set, use it
  if (props.message.isReadByCurrentUser !== undefined) {
    return !props.message.isReadByCurrentUser;
  }

  // If read receipt data hasn't been synced yet, don't show indicator
  return false;
});
</script>

<template>
  <div v-if="tombstone" class="message-wrapper" :class="isSent ? 'mine-row' : 'them-row'">
    <div :class="['bubble', 'tombstone', isSent ? 'mine' : 'them']" :aria-label="tombstoneText(message)">
      <div v-if="!isSent" class="sender-name">{{ senderName }}</div>
      <div class="message-text tombstone-text">
        <v-icon size="14" class="mr-1">mdi-cancel</v-icon>{{ tombstoneText(message) }}
      </div>
      <div class="message-meta text-caption d-flex align-center justify-end mt-1">
        <span class="message-time">{{ formattedTime }}</span>
      </div>
    </div>
  </div>
  <div v-else class="message-wrapper" :class="isSent ? 'mine-row' : 'them-row'">
      <!-- My own sent message: Delete, always for everyone -->
      <v-menu v-if="canDelete">
        <template #activator="{ props: menuProps }">
          <button type="button" class="bubble-more mine-more" v-bind="menuProps" aria-label="Options for your message">
            <v-icon size="16">mdi-dots-horizontal</v-icon>
          </button>
        </template>
        <v-list density="compact">
          <v-list-item @click="confirmingDelete = true">
            <template #prepend><v-icon size="18" color="error">mdi-delete-outline</v-icon></template>
            <v-list-item-title>Delete</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <tl-dialog v-if="canDelete" v-model="confirmingDelete" :title="DELETE_MESSAGE_COPY.title" :max-width="440">
        <p class="block-confirm-text">{{ DELETE_MESSAGE_COPY.body }}</p>
        <div class="block-confirm-actions">
          <v-btn variant="text" @click="confirmingDelete = false">{{ DELETE_MESSAGE_COPY.cancel }}</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">{{ DELETE_MESSAGE_COPY.confirm }}</v-btn>
        </div>
      </tl-dialog>
        <!-- Message content bubble -->
      <div :class="['bubble', isSent ? 'mine' : 'them', isImageMessage ? 'media' : '', showUnreadIndicator ? 'unread' : '']">
        <!-- Unread indicator dot for received messages -->
        <div v-if="showUnreadIndicator" class="unread-indicator"></div>

        <!-- Sender name for received messages, with the report/block menu -->
        <div v-if="!isSent" :class="['sender-name', 'd-flex', 'align-center', isImageMessage ? 'px-3 pt-2' : '']">
          <span class="flex-grow-1">{{ senderName }}</span>
          <v-menu v-if="conversationId && message.senderUserId">
            <template #activator="{ props: menuProps }">
              <button type="button" class="bubble-more" v-bind="menuProps" :aria-label="`Options for message from ${senderName}`">
                <v-icon size="16">mdi-dots-horizontal</v-icon>
              </button>
            </template>
            <v-list density="compact">
              <v-list-item @click="showReportDialog = true">
                <template #prepend><v-icon size="18">mdi-flag-outline</v-icon></template>
                <v-list-item-title>Report message</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="blockBusy" @click="toggleBlock">
                <template #prepend><v-icon size="18" :color="senderBlocked ? undefined : 'error'">{{ senderBlocked ? 'mdi-account-check-outline' : 'mdi-account-cancel-outline' }}</v-icon></template>
                <v-list-item-title>{{ senderBlocked ? `Unblock ${senderName}` : `Block ${senderName}` }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <ReportDialog
              v-if="conversationId"
              v-model:open="showReportDialog"
              :conversation-id="conversationId"
              :message-id="message.id"
              :preview="isImageMessage ? '(an image)' : message.content"
          />
          <tl-dialog v-if="conversationId" v-model="confirmingBlock" :title="blockCopy.title" :max-width="440">
            <p class="block-confirm-text">{{ blockCopy.body }}</p>
            <div class="block-confirm-actions">
              <v-btn variant="text" :disabled="blockBusy" @click="confirmingBlock = false">Cancel</v-btn>
              <v-btn color="error" variant="flat" :loading="blockBusy" @click="confirmBlock">{{ blockCopy.confirm }}</v-btn>
            </div>
          </tl-dialog>
        </div>

        <!-- Image message -->
        <div v-if="isImageMessage" class="message-image-container">
          <!-- Image (all states) -->
          <div
            v-if="imageUrl"
            :class="['image-wrapper', {
              'image-failed': isImageUploadFailed
            }]"
          >
            <v-img
              :src="imageUrl"
              :aspect-ratio="imageAspectRatio"
              max-width="300"
              max-height="400"
              class="message-image rounded"
              :class="{ 'cursor-pointer': !isImageUploading && !isImageUploadFailed }"
              cover
              @click="!isImageUploading && !isImageUploadFailed && (showLightbox = true)"
            >
              <template #placeholder>
                <div class="d-flex align-center justify-center fill-height">
                  <v-progress-circular indeterminate :color="`rgb(var(--v-theme-textSubtle))`" />
                </div>
              </template>
              <template #error>
                <div class="d-flex align-center justify-center fill-height" style="background: rgb(var(--v-theme-light));">
                  <v-icon>mdi-image-broken</v-icon>
                </div>
              </template>
            </v-img>

            <!-- Uploading overlay -->
            <div v-if="isImageUploading" class="image-overlay uploading-overlay">
              <v-progress-circular
                indeterminate
                color="white"
                size="32"
                class="uploading-spinner"
              />
              <div class="text-caption mt-2">Uploading...</div>
            </div>

            <!-- Gradient overlay for timestamp/status -->
            <div class="image-meta-gradient"></div>

            <!-- Timestamp and status (overlaid on image) -->
            <div class="message-meta-overlay text-caption d-flex align-center justify-end">
              <span class="message-time">
                {{ formattedTime }}
              </span>

              <!-- Status indicators for sent messages -->
              <template v-if="isSent">
                <!-- Pending -->
                <v-icon
                  v-if="message._syncState === 'pending'"
                  size="14"
                  class="ml-1"
                  color="white"
                >
                  mdi-clock-outline
                </v-icon>

                <!-- Failed -->
                <v-icon
                  v-else-if="message._syncState === 'failed'"
                  size="14"
                  class="ml-1"
                  color="error"
                >
                  mdi-alert-circle
                </v-icon>

                <!-- Synced / Read -->
                <template v-else>
                  <v-icon
                    v-if="message.readByCount > 0"
                    size="14"
                    class="ml-1 cursor-pointer"
                    color="white"
                    @click="handleReadIndicatorClick"
                  >
                    mdi-check-all
                  </v-icon>
                  <v-icon
                    v-else
                    size="14"
                    class="ml-1"
                    color="white"
                  >
                    mdi-check
                  </v-icon>
                </template>
              </template>
            </div>
          </div>

          <!-- Caption text (if provided) -->
          <div v-if="message.content && message.content.trim()" class="image-caption mt-2">
            {{ message.content }}
          </div>
        </div>

        <!-- Text message -->
        <div v-else class="message-text">
          {{ message.content }}
        </div>

        <!-- Timestamp and status (only for text messages, image has it overlaid) -->
        <div v-if="!isImageMessage" class="message-meta text-caption d-flex align-center justify-end mt-1">
          <span class="message-time">
            {{ formattedTime }}
          </span>

          <!-- Status indicators for sent messages -->
          <template v-if="isSent">
            <!-- Pending -->
            <v-icon
              v-if="message._syncState === 'pending'"
              size="14"
              class="ml-1"
              :color="`rgb(var(--v-theme-textSubtle))`"
            >
              mdi-clock-outline
            </v-icon>

            <!-- Failed -->
            <v-icon
              v-else-if="message._syncState === 'failed'"
              size="14"
              class="ml-1"
              color="error"
            >
              mdi-alert-circle
            </v-icon>

            <!-- Synced / Read -->
            <template v-else>
              <v-icon
                v-if="message.readByCount > 0"
                size="14"
                class="ml-1 cursor-pointer"
                color="white"
                @click="handleReadIndicatorClick"
              >
                mdi-check-all
              </v-icon>
              <v-icon
                v-else
                size="14"
                class="ml-1"
                :color="`rgb(var(--v-theme-textSubtle))`"
              >
                mdi-check
              </v-icon>
            </template>
          </template>
        </div>

        <!-- Read by dialog -->
        <v-dialog v-model="showReadByDialog" max-width="500">
          <v-card
            :elevation="0"
            style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:14px;"
          >
            <v-card-title class="d-flex align-center justify-space-between" style="border-bottom: 1px solid rgb(var(--v-theme-borderLight));">
              <span>Read by</span>
              <v-btn icon="mdi-close" variant="text" size="small" @click="showReadByDialog = false"></v-btn>
            </v-card-title>
            <v-card-text v-if="loadingReadByUsers" class="text-center py-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </v-card-text>
            <div v-else class="pa-3">
              <user-card
                v-for="user in readByUsers"
                :key="user.id"
                :user="user"
                class="mb-2"
              />
              <div v-if="readByUsers.length === 0" class="text-center py-4" style="color: rgb(var(--v-theme-textSubtle));">
                No one has read this message yet
              </div>
            </div>
          </v-card>
        </v-dialog>
      </div>

  <!-- Error actions below bubble (for failed messages) -->
  <div v-if="message._syncState === 'failed'" class="message-error-actions">
    <div class="text-caption text-error mb-1">
      {{ message._errorMessage || 'Failed to send' }}
    </div>
    <div class="d-flex ga-2">
      <v-btn
        size="x-small"
        color="error"
        variant="tonal"
        prepend-icon="mdi-refresh"
        @click="$emit('retry', message)"
      >
        Retry
      </v-btn>
      <v-btn
        size="x-small"
        color="error"
        variant="text"
        prepend-icon="mdi-delete"
        @click="$emit('delete', message)"
      >
        Delete
      </v-btn>
    </div>
  </div>

  <!-- Image Lightbox Dialog -->
  <v-dialog v-model="showLightbox" max-width="90vw">
    <v-card
      :elevation="0"
      style="border:1px solid rgb(var(--v-theme-borderLight)); border-radius:14px;"
    >
      <v-card-title class="d-flex align-center justify-space-between pa-3" style="border-bottom: 1px solid rgb(var(--v-theme-borderLight));">
        <span class="text-subtitle-2">{{ senderName }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="showLightbox = false"
        />
      </v-card-title>
      <v-card-text class="pa-0">
        <v-img
          v-if="lightboxImageUrl"
          :src="lightboxImageUrl"
          max-height="80vh"
          contain
        >
          <template #placeholder>
            <div class="d-flex align-center justify-center fill-height">
              <v-progress-circular indeterminate color="primary" />
            </div>
          </template>
          <template #error>
            <div class="d-flex align-center justify-center fill-height" style="min-height: 300px; background: rgb(var(--v-theme-light));">
              <div class="text-center">
                <v-icon size="48" :color="`rgb(var(--v-theme-textSubtle))`">mdi-image-broken</v-icon>
                <div class="text-caption mt-2" style="color: rgb(var(--v-theme-textSubtle));">Failed to load image</div>
              </div>
            </div>
          </template>
        </v-img>
        <div v-else class="d-flex align-center justify-center pa-8">
          <v-img
            :src="imageUrl"
            max-height="80vh"
            contain
          />
        </div>
      </v-card-text>
      <v-card-text
        v-if="message.content && message.content.trim()"
        class="pa-3"
        style="border-top: 1px solid rgb(var(--v-theme-borderLight));"
      >
        <div class="text-body-2">{{ message.content }}</div>
      </v-card-text>
    </v-card>
  </v-dialog>
</div>
</template>

<style scoped>
/* row wrapper aligns the bubble left (them) / right (mine) */
.message-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.them-row {
  align-items: flex-start;
}

.mine-row {
  /* a row, so my own message's options button sits beside the bubble */
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.mine-more {
  margin: 0 4px 0 0;
}

/* a deleted message's placeholder: muted, italic, not selectable */
.bubble.tombstone.mine,
.bubble.tombstone.them {
  background: transparent;
  border: 1px dashed rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-textSubtle));
  user-select: none;
  -webkit-user-select: none;
}

.message-wrapper .bubble.tombstone .message-text,
.message-wrapper .bubble.tombstone .message-time,
.message-wrapper .bubble.tombstone .sender-name {
  color: rgb(var(--v-theme-textSubtle));
}

.tombstone-text {
  font-style: italic;
}

/* bubble (mockup .bubble) */
.bubble {
  position: relative;
  max-width: 76%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.45;
  word-wrap: break-word;
}

.bubble.them {
  background: rgb(var(--v-theme-bg, 250, 250, 250));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-bottom-left-radius: 6px;
  color: rgb(var(--v-theme-text));
}

.bubble.mine {
  background: rgb(var(--v-theme-primary));
  color: #fff;
  border-bottom-right-radius: 6px;
}

/* image bubbles: tight padding, capped width */
.bubble.media {
  padding: 5px;
  width: min(76%, 320px);
}

.bubble.mine .message-text,
.bubble.mine .message-time {
  color: #fff;
}

.bubble.mine .message-meta .v-icon:not(.text-error) {
  color: #fff !important;
}

.sender-name {
  font-size: 11px;
  font-weight: 600;
  opacity: 0.8;
  margin-bottom: 2px;
}

.message-text {
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.image-caption {
  font-size: 13px;
  line-height: 1.45;
  padding: 4px 8px 4px;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 300px;
}

.message-meta {
  font-size: 9.5px;
  opacity: 0.55;
  gap: 4px;
}

.message-time {
  font-size: 9.5px;
}

/* Error actions below bubble */
.message-error-actions {
  padding: 4px 2px 8px;
}

/* Unread indicator */
.unread-indicator {
  position: absolute;
  top: -2px;
  left: -2px;
  width: 6px;
  height: 6px;
  background-color: rgb(var(--v-theme-primary));
  border-radius: 50%;
  border: 1px solid white;
  z-index: 1;
}

/* Image message styles */
.message-image-container {
  position: relative;
}

.image-wrapper {
  position: relative;
  display: block;
  border-radius: 12px;
  overflow: hidden;
  min-width: 200px;
  min-height: 150px;
}

.bubble.mine .image-wrapper {
  border-bottom-right-radius: 6px;
}

.bubble.them .image-wrapper {
  border-bottom-left-radius: 6px;
}

.image-wrapper.image-failed {
  box-shadow: inset 0 0 0 2px rgba(var(--v-theme-error), 0.4);
}

.image-wrapper .message-image {
  display: block;
  width: 100%;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: white;
}

.uploading-overlay {
  background-color: rgba(0, 0, 0, 0.5);
}

.failed-overlay {
  background-color: rgba(0, 0, 0, 0.15);
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;
}

.cursor-pointer:hover {
  opacity: 0.9;
}

.uploading-spinner {
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.3));
}

/* Gradient overlay for image meta info */
.image-meta-gradient {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
  pointer-events: none;
}

/* Overlaid meta information on images */
.message-meta-overlay {
  position: absolute;
  bottom: 4px;
  right: 8px;
  color: white;
  font-size: 0.6rem;
  opacity: 0.9;
  gap: 4px;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}
.bubble-more {
  background: none; border: 0; padding: 2px; margin-left: 6px; line-height: 0;
  color: rgb(var(--v-theme-textSubtle)); opacity: .55; cursor: pointer; border-radius: 4px;
}
.bubble-more:hover, .bubble-more:focus-visible { opacity: 1; outline: none; background: rgba(0,0,0,.06); }
.block-confirm-text { font-size: 14px; line-height: 1.5; max-width: 62ch; margin: 0; white-space: normal; }
.block-confirm-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 18px; }
</style>
